import { Button, Flex, Form, Input, Space, Spin } from "antd"
import PropTypes from "prop-types"
import { colors, spacings } from "themes/variables"
import { Text } from "ui/Typography"
import { useDispatch, useSelector } from "react-redux"
import { useEffect, useState } from "react"
import { createGuideBookingIncomeRecord, getClientBookingDetails } from "store/bookings"
import { toaster } from "components/common/Toast"
import { numberToCurrency } from "helpers/string"

const CashPaymentForm = ({ remainingBalance, bookingUUID, bookingId, onClose, onSubmitClose, onUnsavedChangesChange }) => {
  const [form] = Form.useForm()
  const [hasErrors, setHasErrors] = useState(true)
  const dispatch = useDispatch()
  const { loading } = useSelector((state) => state.bookings.incomes)

  const amountToPay = Form.useWatch("amount", form)
  const hasUnsavedChanges = Boolean(amountToPay)

  useEffect(() => {
    onUnsavedChangesChange?.(hasUnsavedChanges)
  }, [hasUnsavedChanges, onUnsavedChangesChange])

  const handleSubmit = (data) => {
    dispatch(createGuideBookingIncomeRecord(bookingId, { amount: Number(data?.amount), stripe: false }))
      .then((response) => {
        if (response?.status === "confirmed") {
          toaster.success("Payment recorded!")
          dispatch(getClientBookingDetails(bookingUUID))
        }
      })
      .finally(() => {
        form.resetFields()
        onUnsavedChangesChange?.(false)
        if (onSubmitClose) {
          onSubmitClose()
          return
        }
        onClose?.()
      })
  }

  const handleFieldsChange = () => {
    const errors = form.getFieldsError().some(({ errors }) => errors.length)
    setHasErrors(errors)
  }

  return (
    <Spin spinning={loading} tip="Processing payment...">
      <Space vertical size={24} className="w-full" direction="vertical">
        <Flex gap={spacings.S} style={{ backgroundColor: colors.surface1 }} className="py-xs px-s">
          <Text color={colors.textSecondaryDark}>Outstanding Balance: </Text>
          <Text color={colors.textPrimary}>{numberToCurrency(remainingBalance)}</Text>
        </Flex>
        <Form layout="vertical" form={form} onFinish={handleSubmit} onFieldsChange={handleFieldsChange}>
          <Form.Item label="Amount to Pay*">
            <Form.Item
              name="amount"
              noStyle
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value) {
                      return Promise.reject(new Error("Amount to Pay is required"))
                    }
                    const numericValue = Number.parseFloat(value)
                    if (Number.isNaN(numericValue)) {
                      return Promise.reject(new Error("Please enter a valid number"))
                    }
                    if (numericValue > remainingBalance) {
                      return Promise.reject(new Error("Cannot exceed outstanding balance."))
                    }
                    if (numericValue <= 0) {
                      return Promise.reject(new Error("Amount must be greater than zero"))
                    }
                    return Promise.resolve()
                  }
                })
              ]}
            >
              <Input placeholder="Amount to Pay" prefix="$" type="number" min="0.01" step="0.01" />
            </Form.Item>
          </Form.Item>
          <Flex gap={spacings.S} className="mt-3xl">
            <Button className="py-l px-xl" color="primary" variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <Button type="primary" className="py-l px-xl" htmlType="submit" disabled={hasErrors}>
              Submit
            </Button>
          </Flex>
        </Form>
      </Space>
    </Spin>
  )
}

CashPaymentForm.propTypes = {
  remainingBalance: PropTypes.number,
  bookingUUID: PropTypes.string,
  bookingId: PropTypes.number,
  onClose: PropTypes.func,
  onSubmitClose: PropTypes.func,
  onUnsavedChangesChange: PropTypes.func
}

export default CashPaymentForm
